// `GET/PUT /api/business-profile` -- D8's business context, design §6.1.
// `GET` returns the `tax_year IS NULL` (current) row, or `null` when the
// user has never set one -- null, not an empty-string placeholder, so the
// Business tab can tell "no profile yet" from "profile with blank fields"
// (data-honesty rule: an honest state is not a zero/empty substitute).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

export const businessProfileRowSchema = z.object({
  business_type: z.string().nullable(),
  description: z.string().nullable(),
  updated_at: z.string(),
});
export type BusinessProfileRow = z.infer<typeof businessProfileRowSchema>;

const businessProfileResponseSchema = businessProfileRowSchema.nullable();

export interface SaveBusinessProfileInput {
  business_type: string | null;
  description: string | null;
}

export function useBusinessProfile() {
  return useQuery<BusinessProfileRow | null>({
    queryKey: ['business-profile'],
    queryFn: async () => {
      const response = await client.get('/api/business-profile');
      return parseEnvelope(businessProfileResponseSchema, response.data).data;
    },
  });
}

export function useSaveBusinessProfile() {
  const queryClient = useQueryClient();
  return useMutation<BusinessProfileRow, Error, SaveBusinessProfileInput>({
    mutationFn: async (input) => {
      const response = await client.put('/api/business-profile', input);
      return parseEnvelope(businessProfileRowSchema, response.data).data;
    },
    onSuccess: (row) => {
      queryClient.setQueryData(['business-profile'], row);
    },
  });
}
